import companyApiClient from "../../api/companyApiClient";
import baseUrl from "../../api/baseUrl";

export interface PaymentOrder {
  orderId: string;
  companyId: number;
  subscriptionId?: number;
  amount: number;
  currency: string;
  months: number;
  companyName: string;
  planName: string;
  hash: string; // MD5 hash for PayHere
  merchantId: string; // Merchant ID
}

export interface Payment {
  id: number;
  companyId: number;
  subscriptionId?: number;
  amount: number;
  currency: string;
  paymentMethod: string;
  paymentStatus: string;
  transactionId: string;
  payHereOrderId: string;
  payHerePaymentId: string;
  paymentDate: string;
}

export interface Invoice {
  id: number;
  companyId: number;
  subscriptionId: number;
  invoiceNumber: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  amount: number;
  currency: string;
  status: "unpaid" | "paid" | "overdue" | "cancelled";
  dueDate: string;
  paidAt?: string;
  createdAt: string;
}

const paymentService = {
  // Create payment order
  createPaymentOrder: async (data: {
    companyId: number;
    subscriptionId?: number;
    amount: number;
    currency: string;
    months: number;
  }): Promise<PaymentOrder> => {
    try {
      const response = await companyApiClient.post(
        "/company/payment/order",
        data,
      );
      return response.data.data.order;
    } catch (error) {
      console.error("Error creating payment order:", error);
      throw error;
    }
  },

  // Get payment by order ID
  getPaymentByOrderId: async (orderId: string): Promise<Payment | null> => {
    try {
      const response = await companyApiClient.get(
        `/company/payment/order/${orderId}`,
      );
      return response.data.data.payment;
    } catch (error) {
      console.error("Error fetching payment:", error);
      throw error;
    }
  },

  // Get company payments
  getCompanyPayments: async (companyId: string): Promise<Payment[]> => {
    try {
      const response = await companyApiClient.get(
        `/company/payments/company/${companyId}`,
      );
      return response.data.data.payments || [];
    } catch (error) {
      console.error("Error fetching company payments:", error);
      throw error;
    }
  },

  // Get company invoices (for company dashboard - uses company token)
  getCompanyInvoices: async (companyId: string): Promise<Invoice[]> => {
    try {
      const response = await companyApiClient.get(
        `/company/invoices/company/${companyId}`,
      );
      return response.data.data.invoices || [];
    } catch (error) {
      console.error("Error fetching company invoices:", error);
      throw error;
    }
  },

  // Get invoices from branch context (uses branch token)
  getInvoicesFromBranch: async (companyId: string): Promise<Invoice[]> => {
    try {
      const response = await baseUrl.get(
        `/branch/subscription/invoices/company/${companyId}`,
      );
      return response.data.data.invoices || [];
    } catch (error) {
      console.error("Error fetching invoices from branch:", error);
      throw error;
    }
  },

  // Get company payments (for company dashboard - uses company token)
  getCompanyPaymentsFromCompany: async (
    companyId: string,
  ): Promise<Payment[]> => {
    try {
      const response = await companyApiClient.get(
        `/company/payments/company/${companyId}`,
      );
      return response.data.data.payments || [];
    } catch (error) {
      console.error("Error fetching company payments:", error);
      throw error;
    }
  },

  // Get payments from branch context (uses branch token)
  getPaymentsFromBranch: async (companyId: string): Promise<Payment[]> => {
    try {
      const response = await baseUrl.get(
        `/branch/subscription/payments/company/${companyId}`,
      );
      return response.data.data.payments || [];
    } catch (error) {
      console.error("Error fetching payments from branch:", error);
      throw error;
    }
  },

  // ⚠️ TEST ONLY - Simulate successful payment (DELETE IN PRODUCTION!)
  testPaymentWebhook: async (data: {
    orderId: string;
    companyId: number;
    subscriptionId?: number;
    amount: number;
    currency: string;
    months: number;
  }): Promise<any> => {
    try {
      const response = await companyApiClient.post(
        "/company/payment/test/webhook",
        data,
      );
      return response.data;
    } catch (error) {
      console.error("Error testing payment webhook:", error);
      throw error;
    }
  },
};

export default paymentService;
